import { Chip, Tooltip, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { peopleByCompanyStateJ } from '../../../services/state/PeopleByCompanyStateJ';
import { FMT } from '../../../util/formatter-service';
import { compareByCreatedAt, getEngagementSummary, IEngagementSummary } from '../utils/getPersonActivityData';
import { IContactActivityItemDataModel } from '../../../data-models/person.data-model';
import { ActivityDetails } from './ActivityDetails';

interface ILastContactedProps {
  companyId: number;
}

export function LastContacted(props: ILastContactedProps) {
  const people = useAtomValue(peopleByCompanyStateJ(props.companyId));

  const lastContact = useMemo(() => {
    if (!people) {
      return null;
    }
    return people.reduce(
      (res, person) => {
        const activity = getEngagementSummary(person.activity);
        if (!activity.lastEmail && !activity.lastMeeting) {
          return res;
        }
        let latest;
        if (activity.lastEmail && activity.lastMeeting) {
          latest =
            compareByCreatedAt(activity.lastEmail, activity.lastMeeting) > 0
              ? activity.lastEmail
              : activity.lastMeeting;
        } else if (activity.lastEmail) {
          latest = activity.lastEmail;
        } else {
          latest = activity.lastMeeting;
        }

        // Compare with the current result to find the most recent contact across all people
        if (!res || !res.lastContact) {
          return {
            lastContact: latest,
            contactType: latest === activity.lastEmail ? 'email' : 'meeting',
            activity,
          } as ILastEmailDetailsProps;
        }

        // Compare dates to find the most recent contact
        const currentDate = res.lastContact.createdAt ? new Date(res.lastContact.createdAt).getTime() : 0;
        const latestDate = latest?.createdAt ? new Date(latest.createdAt).getTime() : 0;

        if (latestDate > currentDate) {
          return {
            lastContact: latest,
            contactType: latest === activity.lastEmail ? 'email' : 'meeting',
            activity,
          } as ILastEmailDetailsProps;
        }

        return res;
      },
      null as ILastEmailDetailsProps | null
    );
  }, [people]);

  return lastContact ? <LastContactDetails {...lastContact} /> : <div />;
}

interface ILastEmailDetailsProps {
  lastContact: IContactActivityItemDataModel | null;
  contactType: 'email' | 'meeting';
  activity: IEngagementSummary;
}
function LastContactDetails(props: ILastEmailDetailsProps) {
  const theme = useTheme();
  if (!props.lastContact) {
    return null;
  }
  const { lastContact, contactType, activity } = props;
  const contactTypeText = contactType === 'email' ? 'emailed' : 'met';
  const message = `Last ${contactTypeText} on ${FMT.format('dateShort', lastContact.createdAt!)}`;

  return (
    <Tooltip
      placement='bottom-end'
      componentsProps={{
        tooltip: { sx: { minWidth: '28rem' } },
      }}
      leaveDelay={200}
      title={<ActivityDetails engagementSummary={activity} style={{ padding: '1rem' }} />}
    >
      <Chip
        aria-label={'last contact'}
        size={'small'}
        label={message}
        variant='outlined'
        sx={{ '&:hover': { background: theme.palette.action.hover } }}
      />
    </Tooltip>
  );
}
